import { Meteor } from 'meteor/meteor';
import { Mongo } from 'meteor/mongo';
import { DeclareMethod } from '/imports/methods';
import { Dict, Doc, GetMethodResponse, ListMethodArg, ListMethodResponse, ModelClass, References } from '/imports/types';
import { validateString } from '/imports/validators';
import { strings } from '/imports/strings';
import { validatePreferredChains } from '/imports/api/preferredChains';
import { validateB2bGroups, DEFAULT_B2B_GROUPS_TEXT } from '/imports/api/b2bGroups';

export interface Settings extends Doc {
  _id: string;
  playwrightDisplayServer?: string; // e.g. '142.127.237.64:0' server must have X accepting tcp on port 6000, and router configured to forward the port
  // Goatbots scrape schedules: { '<cron expression>': ['SET', 'CODES', ...] }.
  // Each key is a (6-field) cron string; its value is the set codes scraped on
  // that schedule. Edited as JSON in the UI, stored as an object. Read by
  // goatbotsJobs.ts at server startup to register one cron job per entry.
  goatbotsScrapeSchedules?: Dict<string[]>;
  // Default man buy-price tiers, used when a card has no per-card/per-set
  // margin. Each [cutoff, factor] pair means "if gbBuy > cutoff, manBuy =
  // factor * gbBuy", evaluated highest-cutoff first; the lowest pair is the
  // floor. e.g. [[1, 0.9], [0.1, 0.75], [0, 0.5]]. Edited as JSON.
  goatbotsManBuyTiers?: [number, number][];
  // Divisor for the default man sell-price spread shave:
  // manSell = gbSell - (gbSell - gbBuy) / divisor.
  // 0 disables the shave entirely, i.e. sell at exactly the Goatbots price.
  goatbotsManSellSpreadDivisor?: number;
  // Effective default log verbosity for bots started without an explicit level
  // (1 = Low, 2 = Medium, 3 = High). Read by botJobs.ts when starting a bot.
  /** MTGO client login checksum, extracted from FlsClient.dll by the DevTools
   *  "Client checksum" panel. Read by Packet.login on every bot login; when MTGO
   *  ships a client update this value goes stale and EVERY bot fails to log in,
   *  which is why it lives here rather than in a multi-row collection where the
   *  "which row wins" question could ever be ambiguous. */
  md5?: string;
  /**
   * Set code (e.g. 'HOB') for the set the PUBLIC SITE features: the default
   * /prices set and the "Latest" badge, served by /api/public/featured-set.
   *
   * No longer used for bot advertising -- that moved to `classifiedQuery`
   * below, and to Fleet.classifiedQuery. Kept because the site reads it and a
   * Mongo query cannot answer "which single set is featured".
   */
  featuredSet?: string;
  /**
   * Fallback Mongo query for classified ad card selection, used by any bot
   * whose fleet has no `classifiedQuery` of its own. Run against `cards`.
   */
  classifiedQuery?: Record<string, unknown> | null;
  /** Card list refs replacing the inline queries below (which are legacy:
   *  kept for rollback, no longer read). */
  classifiedCardListId?: string | null;
  buyCardListId?: string | null;
  sellCardListId?: string | null;
  /** LEGACY. See classifiedCardListId. */
  buyQuery?: Record<string, unknown> | null;
  /** Fallback sell/stock selection for fleets without a sellQuery. */
  sellQuery?: Record<string, unknown> | null;
  /** Fallback ownership cap for bought cards (the old universal buycap: 4). */
  buyCap?: number | null;
  /** Fallback stock level for sold cards (overrides bot.options.stocksize). */
  sellCap?: number | null;
  /** Fallback replenish priority for fleets without their own (higher = first). */
  replenishPriority?: number | null;
  /** Floor buy price for cards with NO real buy price (man/gb both absent).
   *  Chain: bot config -> fleet -> here. Blank = don't buy unpriced cards.
   *  Must be > 0; junk buys are taken last and ledgered with junk: true. */
  defaultBuyPrice?: number | null;
  /** Floor SELL price for junk: applied only when a card has NEITHER a real
   *  buy nor a real sell price. Same chain. Blank = junk is not resold. */
  defaultSellPrice?: number | null;
  /** Fleet-wide default for buying on credit past the ticket balance: bots
   *  keep buying when they run out of tickets and settle the shortfall as
   *  store credit. Chain: bot config -> fleet -> here. Off unless set. */
  overdraft?: boolean | null;
  /** How often sell/hybrid bots evaluate their inventory against targets and
   *  restock from depots, in minutes. Blank = 15. */
  replenishIntervalMinutes?: number | null;
  botVerbosity?: number;
  /** Bot-to-bot (eager) trading: the preferred bot chains, comma-separated
   *  MTGO name prefixes, case-insensitive, a leading underscore on the
   *  chain's name ignored -- e.g. "DojoTrade, CardBuying, Cardhoarder,
   *  Manatrader". Courted first by the picker, trusted to bank fractional
   *  credit for us, and given the trusted scoring bonus. Blank = the
   *  built-in default list (server/mogbot/constants EAGER_CREDIT_TRUSTED_RE). */
  preferredB2bChains?: string | null;
  /** B2B trade with ONLY preferred chains: eager bots invite no one else
   *  (every tier, encores and the manual invite). Inbound trades unaffected. */
  b2bPreferredOnly?: boolean | null;
  /** Bot-to-bot (eager) selling by GROUP CYCLING: the bot groups to walk,
   *  one per line, "Group name: bot, bot, bot" (exact MTGO usernames,
   *  case-insensitive). See server/mogbot/eagerGroups. */
  b2bGroups?: string | null;
  /** Walk the groups above instead of rolling over every advertiser: up to
   *  two bots per group, then the next group, nothing outside the list. */
  b2bGroupCycling?: boolean | null;
}
export type Model = Settings;

/** Seed value for fresh installs (and auto-migration of existing settings docs
 *  that predate the field) — mirrors the schedules previously hardcoded in
 *  goatbotsJobs.ts so scraping keeps running without manual configuration. */
export const DEFAULT_GOATBOTS_SCRAPE_SCHEDULES: Dict<string[]> = {
  '0 */10 * * * *': [
    'HOB', 'HOC', 'HOU', 'MSH', 'MSC', 'SPM', 'SPE', 'MAR', 'SOS', 'SOC', 'SOA', 'TMT', 'ECL', 'TLA', 'OM1', 'EOE', 'FIN', 'TDM',
    'DFT', 
  ],
  '0 30 */5 * * *': [
    'FDN', 'DSK', 'BLB', 'OTJ', 'MKM', 'LCI', 'WOE','TMC', 'PZA', 'ECC', 'TLE', 'OMB', 'EOC', 'EOS', 'FIC', 'FCA', 'TDC', 'DRC', 'J25', 
    'DSC', 'BLC', 'BIG', 'OTP', 'OTC', 'MKC', 'LCI',
    'LCC', 'WOT', 'WOC', 'MAT', 'MOM', 'MUL', 'MOC', 'ONE', 'ONC', 'BRO', 'BRR', 'BRC', 'BOT', 'DMU', 'DMC', 'SNC', 'NCC', 'NEO', 'NEC',
    'VOW', 'VOC', 'MID', 'MIC', 'AFR', 'AFC', 'STX', 'STA', 'KHM', 'KHC', 'ZNR', 'ZNC', 'M21', 'IKO', 'THB', 'ELD', 'M20', 'WAR', 'RNA',
    'GRN', 'M19', 'DAR', 'RIX', 'XLN', 'AKH', 'AER', 'KLD', 'EMN', 'SOI', 'OGW', 'BFZ', 'ORI', 'DTK', 'FRF', 'KTK', 'M15', 'JOU',
    'BNG', 'THS', 'M14', 'DGM', 'GTC', 'RTR', 'MH3', 'M3C', 'MH2', 'MH1', 'ACR', 'LTR', 'LTC', 'M13', 'AVR', 'DKA', 'ISD', 'M12', 'NPH',
  ],
  '0 50 */10 * * *': [
    'MBS', 'SOM', 'M11', 'ROE', 'WWK', 'ZEN', 'M10', 'ARB', 'CON', 'ALA', 'EVE', 'SHM', 'MOR', 'LRW', '10E', 'FUT', 'PLC', 'TSB', 'TSP',
    'CSP', 'DIS', 'GPT', 'RAV', '9ED', 'SOK', 'BOK', 'CHK', '5DN', 'DST', 'MRD', '8ED', 'SCG', 'LGN', 'ONS', 'JUD', 'TOR', 'OD', 'AP', '7E',
    'PS', 'IN', 'PR', 'NE', 'MM', 'UD', 'UL', 'UZ', 'EX', 'ST', 'TE', 'WL', 'VI', 'MI', 'ALL', 'ICE', '1E', 'ME4', 'ME3', 'ME2', 'MED', 'VMA',
    '2X2', '2XM', 'UMA', 'A25', 'IMA', 'EMA', 'MM3', 'MM2', 'MMA', 'RVR', 'DMR', 'TPR', 'TSR', 'INR', 'PIP', 'CLU', 'REX', '40K', 'CLB',
    'PZ2', 'PZ1', 'SCD', 'BBD', 'JMP', 'J22', 'CN2', 'CMM', 'CMR', 'C21', 'C20', 'C19', 'C17', 'C14', 'C13', 'CMD', 'CC1', 'SPG', 'MB2',
    'UNF', 'UNH', 'VAN', 'PRM_25', 'PRM_RTO', 'PRM', 'DBL', 'SLD', 'SL2', 'SLX', 'MS4', 'MS3', 'MS2', 'ZNE', 'EXP', 'PK', 'PO', 'UST', 'UND',
    'PC2', 'PC1', 'DDM', 'DDL', 'DDK', 'DDJ', 'DDI', 'DDH', 'DDG', 'DDF', 'DDE', 'DDD', 'DDC', 'DD2', 'EVG', 'TD2', 'TD0', 'W16', 'W17', 'G18',
    'GN2', 'PD3', 'V17', 'V16', 'V15', 'V14', 'V13', 'V12', 'V11', 'V10',
  ],
};

/** Validate a parsed goatbotsScrapeSchedules value is a Dict<string[]>.
 *  Returns an error string, or null when valid. Shared by the model's
 *  server-side validate and the Settings UI's TextArea field. */
export const validateScrapeSchedules = (value: unknown): string | null => {
  if (value === undefined || value === null) return null;
  if (typeof value !== 'object' || Array.isArray(value)) return 'Must be a JSON object of { "cron": ["SET", ...] }';
  for (const [key, arr] of Object.entries(value as Dict<unknown>)) {
    if (!Array.isArray(arr) || !arr.every((s) => typeof s === 'string')) {
      return `"${key}" must be an array of set-code strings`;
    }
  }
  return null;
};

/** Default man buy-price tiers, mirroring the strategy previously hardcoded in
 *  goatbotsJobs.ts: >1 -> 0.9, >0.1 -> 0.75, else 0.5. */
export const DEFAULT_GOATBOTS_MAN_BUY_TIERS: [number, number][] = [[1, 0.9], [0.1, 0.75], [0, 0.5]];
export const DEFAULT_GOATBOTS_MAN_SELL_SPREAD_DIVISOR = 8;

/** Bot log verbosity levels and the settings-page pick list. Mirrors the
 *  server-only VLOW/VMED/VHIGH in server/mogbot/constants.ts, redeclared here so
 *  the (isomorphic) Settings model + UI don't import server code. */
export const DEFAULT_BOT_VERBOSITY = 1;
export const BOT_VERBOSITY_OPTIONS: { value: number; label: string }[] = [
  { value: 1, label: 'Low (1)' },
  { value: 2, label: 'Medium (2)' },
  { value: 3, label: 'High (3)' },
];

/** Validate a parsed goatbotsManBuyTiers value is a non-empty array of
 *  [cutoff, factor] number pairs (factor > 0). Returns an error or null. */
export const validateManBuyTiers = (value: unknown): string | null => {
  if (value === undefined || value === null) return null;
  if (!Array.isArray(value) || !value.length) return 'Must be a non-empty JSON array of [cutoff, factor] pairs';
  for (const pair of value) {
    if (!Array.isArray(pair) || pair.length !== 2 || typeof pair[0] !== 'number' || typeof pair[1] !== 'number') {
      return 'Each tier must be a [cutoff, factor] pair of numbers, e.g. [1, 0.9]';
    }
    if (pair[1] <= 0) return 'Each factor must be > 0';
  }
  return null;
};

export const Settings = new Mongo.Collection<Settings>('settings');

export const settingsId = 'settings';
export const settings = async () => await Settings.findOneAsync(settingsId);

type Refs = References<Model>

export const modelClass: ModelClass<Model, Refs> = {
  collection: Settings,
  editableFields: [
    'playwrightDisplayServer',
    'goatbotsScrapeSchedules',
    'goatbotsManBuyTiers',
    'goatbotsManSellSpreadDivisor',
    'botVerbosity',
    'md5',
    'featuredSet',
    'classifiedCardListId',
    'buyCardListId',
    'sellCardListId',
    'buyCap',
    'sellCap',
    'replenishPriority',
    'defaultBuyPrice',
    'defaultSellPrice',
    'overdraft',
    'replenishIntervalMinutes',
    'preferredB2bChains',
    'b2bPreferredOnly',
    'b2bGroups',
    'b2bGroupCycling',
  ],
  listMethod: DeclareMethod<ListMethodArg<Settings>, ListMethodResponse<Settings, Refs>>('listSettings'),
  getMethod: DeclareMethod<string, GetMethodResponse<Settings, Refs>>('getSettings'),
  createMethod: DeclareMethod<Partial<Settings>, string>('createSettings'),
  updateMethod: DeclareMethod<Partial<Settings>, string>('updateSettings'),
  deleteMethod: DeclareMethod<string>('deleteSettings'),
  pathPrefix: '/settings',
  modelName: strings.Settings(),
  pluralName: strings.Settings(),
  validate: async (settings: Partial<Settings>) => {
    const errors: { [key: string]: string[] } = {};
    settings._id && validateString(errors, settings, '_id');
    validateString(errors, settings, 'playwrightDisplayServer');
    if ('goatbotsScrapeSchedules' in settings) {
      const err = validateScrapeSchedules(settings.goatbotsScrapeSchedules);
      if (err) errors.goatbotsScrapeSchedules = [err];
    }
    if ('goatbotsManBuyTiers' in settings) {
      const err = validateManBuyTiers(settings.goatbotsManBuyTiers);
      if (err) errors.goatbotsManBuyTiers = [err];
    }
    if ('goatbotsManSellSpreadDivisor' in settings) {
      const d = settings.goatbotsManSellSpreadDivisor;
      if (d !== undefined && d !== null && (typeof d !== 'number' || !Number.isFinite(d) || d < 0)) {
        errors.goatbotsManSellSpreadDivisor = ['Must be a number >= 0 (0 = sell at the Goatbots price)'];
      }
    }
    if ('defaultBuyPrice' in settings) {
      const d = settings.defaultBuyPrice;
      if (d !== undefined && d !== null && (typeof d !== 'number' || !Number.isFinite(d) || d <= 0)) {
        errors.defaultBuyPrice = ['Must be a number > 0, or blank to not buy unpriced cards'];
      }
    }
    if ('defaultSellPrice' in settings) {
      const d = settings.defaultSellPrice;
      if (d !== undefined && d !== null && (typeof d !== 'number' || !Number.isFinite(d) || d <= 0)) {
        errors.defaultSellPrice = ['Must be a number > 0, or blank to not resell junk'];
      }
    }
    if ('preferredB2bChains' in settings) {
      const err = validatePreferredChains(settings.preferredB2bChains);
      if (err) errors.preferredB2bChains = [err];
    }
    if ('b2bGroups' in settings) {
      const err = validateB2bGroups(settings.b2bGroups);
      if (err) errors.b2bGroups = [err];
    }
    if ('botVerbosity' in settings) {
      const v = settings.botVerbosity;
      if (v !== undefined && v !== null && !BOT_VERBOSITY_OPTIONS.some((o) => o.value === v)) {
        errors.botVerbosity = ['Must be 1 (Low), 2 (Medium), or 3 (High)'];
      }
    }
    return errors;
  },
};

Meteor.startup(async () => {
  if (Meteor.isClient) return;
  if (process.env.THREAD) return;
  const existing = await Settings.findOneAsync(settingsId);
  if (!existing) {
    await Settings.insertAsync({
      _id: settingsId,
      goatbotsScrapeSchedules: DEFAULT_GOATBOTS_SCRAPE_SCHEDULES,
      goatbotsManBuyTiers: DEFAULT_GOATBOTS_MAN_BUY_TIERS,
      goatbotsManSellSpreadDivisor: DEFAULT_GOATBOTS_MAN_SELL_SPREAD_DIVISOR,
      botVerbosity: DEFAULT_BOT_VERBOSITY,
      b2bGroups: DEFAULT_B2B_GROUPS_TEXT,
    });
    return;
  }
  // Auto-migrate older settings docs that predate a field so scraping keeps
  // running with the prior defaults. Once a field exists this never overwrites
  // it.
  const $set: Partial<Settings> = {};
  if (existing.goatbotsScrapeSchedules === undefined) $set.goatbotsScrapeSchedules = DEFAULT_GOATBOTS_SCRAPE_SCHEDULES;
  if (existing.goatbotsManBuyTiers === undefined) $set.goatbotsManBuyTiers = DEFAULT_GOATBOTS_MAN_BUY_TIERS;
  if (existing.goatbotsManSellSpreadDivisor === undefined) $set.goatbotsManSellSpreadDivisor = DEFAULT_GOATBOTS_MAN_SELL_SPREAD_DIVISOR;
  if (existing.botVerbosity === undefined) $set.botVerbosity = DEFAULT_BOT_VERBOSITY;
  if (existing.b2bGroups === undefined) $set.b2bGroups = DEFAULT_B2B_GROUPS_TEXT;
  if (Object.keys($set).length) await Settings.updateAsync(settingsId, { $set });
});
